import { Box, Typography } from '@mui/material';
import { RefreshCw, Search } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactElement, type SyntheticEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { createAdminApi, type AdminFamilySummary } from '@/admin/adminApi';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { hasNextPage } from '@/admin/users/accountPresentation';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import { useHttpClient } from '@/app/providers';
import {
  AccessDenied,
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  LoadingState,
  MasterDetail,
  PageHeader,
  SelectField,
  Switch,
  useDetailRoute,
  type DataTableColumn,
} from '@/ui/kit';

import { FamilyDetailPanel } from './FamilyDetailPanel';
import {
  ANY_STATE,
  DEFAULT_FAMILY_FILTERS,
  FAMILY_STATE_OPTIONS,
  describeFamiliesPage,
  familySubtitle,
  hasActiveFamilyFilter,
  readFamilyFilters,
  readFamilyPage,
  writeFamilyFilters,
  type FamilyFilters,
} from './familiesPresentation';
import { FAMILIES_PATH } from './routes';

/**
 * Familles et partages (`/families`, maquette P07, BO-17).
 *
 * Un profil bebe et les comptes qui y ont acces. Cinq partis pris :
 *
 * 1. **Lecture seule, sans exception.** Aucun bouton ne modifie un role, ne
 *    retire un membre ni ne revoque une invitation, pour `support` comme pour
 *    `admin` : c'est le geste du titulaire dans l'application, et
 *    l'administration ne s'y substitue que par une decision produit et
 *    juridique (#525).
 * 2. **La recherche est un geste explicite** (Entree ou bouton) : par prenom ou
 *    par adresse de membre, elle enumere des enfants, et chaque requete s'ecrit
 *    au registre des acces (ADR 0032) — une requete par frappe le noierait.
 * 3. **Le total et l'age sont ceux du serveur.** Rien n'est calcule ici ; une
 *    donnee absente s'ecrit « — ».
 * 4. **Les filtres vivent dans l'URL** et la liste reste montee quand la fiche
 *    s'ouvre (`MasterDetail`) : filtres, page et position sont conserves.
 * 5. **Pas de rafraichissement automatique** (ADR 0035 §4) : « Actualiser » et
 *    la relecture au retour sur l'onglet.
 */

const PER_PAGE = 25;

const COLUMNS: readonly DataTableColumn<AdminFamilySummary>[] = [
  {
    key: 'baby',
    header: 'Profil',
    primary: true,
    render: (family) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Avatar name={family.firstName} tone="pink" />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{family.firstName}</Box>
          <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
            {familySubtitle(family)}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    key: 'state',
    header: 'État',
    render: (family) => (family.archived ? <Badge label="Archivé" tone="neutral" /> : <Badge label="Actif" tone="success" dot />),
  },
];

/** Formulaire de recherche : `key={q}` le remet a la valeur de l'URL quand celle-ci change. */
const SearchForm = ({ value, onSubmit }: { readonly value: string; readonly onSubmit: (value: string) => void }): ReactElement => {
  const [draft, setDraft] = useState(value);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    onSubmit(draft.trim());
  };

  return (
    <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 220px' }}>
        <Field
          label="Prénom du bébé ou adresse d’un membre"
          type="search"
          value={draft}
          onChange={setDraft}
          hint="Un fragment suffit, la casse n’a pas d’importance. Laissez vide pour tout lister."
        />
      </Box>
      <Box sx={{ pb: { xs: 0, sm: 3 } }}>
        <Button type="submit" variant="primary" icon={Search}>
          Rechercher
        </Button>
      </Box>
    </Box>
  );
};

export const FamiliesScreen = (): ReactElement => {
  const http = useHttpClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { access, reload: reloadAccess } = useAdminAccess();
  const granted = access.kind === 'granted';

  const filters = useMemo(() => readFamilyFilters(searchParams), [searchParams]);
  const page = readFamilyPage(searchParams);
  const detail = useDetailRoute(FAMILIES_PATH);

  const [token, setToken] = useState(0);
  const refresh = useCallback((): void => {
    setToken((previous) => previous + 1);
  }, []);

  useRefreshOnFocus(refresh);

  const queryKey = `families:${writeFamilyFilters(filters, page).toString()}`;
  const list = useAdminQuery(queryKey, () => createAdminApi(http).listFamilies(filters, page, PER_PAGE), {
    enabled: granted,
    token,
  });

  /** Change les filtres ou la page ; la fiche se ferme, le profil ouvert pouvant sortir de la liste. */
  const go = useCallback(
    (next: FamilyFilters, nextPage = 1): void => {
      void navigate({ pathname: FAMILIES_PATH, search: writeFamilyFilters(next, nextPage).toString() });
    },
    [navigate],
  );

  const result = list.data;
  const items = result?.items ?? [];
  const pageState = {
    total: result?.total ?? 0,
    page: result?.page ?? page,
    perPage: result?.perPage ?? PER_PAGE,
    itemCount: items.length,
  };

  const renderBody = (): ReactElement => {
    if (access.kind === 'loading') {
      return <LoadingState label="Vérification de votre accès à l’administration…" />;
    }

    if (access.kind === 'error') {
      return <ErrorState message={access.message} onRetry={reloadAccess} />;
    }

    if (access.kind === 'denied') {
      return (
        <AccessDenied title="Votre compte n’a pas accès à l’administration">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration : il ne peut donc pas consulter les
          familles. Un rôle s’attribue depuis le serveur, jamais depuis cet écran.
        </AccessDenied>
      );
    }

    const filtered = hasActiveFamilyFilter(filters);

    const listPane = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Card
          title="Rechercher"
          headingLevel="h2"
          subtitle="Profils enregistrés sur le serveur. Un profil jamais repris du journal de synchronisation se lit dans l’explorateur de données."
        >
          <Box component="search" aria-label="Recherche de familles" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SearchForm
              key={filters.search}
              value={filters.search}
              onSubmit={(search) => {
                go({ ...filters, search });
              }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: '1 1 180px', maxWidth: 260 }}>
                <SelectField
                  label="État du profil"
                  value={filters.state ?? ANY_STATE}
                  options={FAMILY_STATE_OPTIONS}
                  onChange={(value) => {
                    go({ ...filters, state: value === 'active' || value === 'archived' ? value : null });
                  }}
                />
              </Box>
              <Switch
                label="Avec invitation en attente"
                checked={filters.pendingInvitation}
                onChange={(checked) => {
                  go({ ...filters, pendingInvitation: checked });
                }}
              />
              {filtered ? (
                <Button
                  size="S"
                  variant="ghost"
                  onClick={() => {
                    go(DEFAULT_FAMILY_FILTERS);
                  }}
                >
                  Réinitialiser les filtres
                </Button>
              ) : null}
            </Box>
          </Box>
        </Card>

        {list.failure === null || list.failure.forbidden ? null : <ErrorState message={list.failure.message} onRetry={refresh} />}
        {list.failure?.forbidden === true ? (
          <AccessDenied title="Votre rôle ne permet pas de consulter les familles">
            Votre compte reste connecté : il manque le rôle d’administration requis.
          </AccessDenied>
        ) : null}

        <Card>
          {result === null && list.busy ? <LoadingState label="Chargement des familles…" /> : null}
          {result === null ? null : (
            <>
              <DataTable<AdminFamilySummary>
                label="Profils bébé"
                columns={COLUMNS}
                rows={items}
                getRowId={(family) => family.id}
                onRowSelect={(family) => {
                  detail.open(family.id);
                }}
                selectedRowId={detail.selectedId}
                emptyMessage={
                  pageState.total > 0
                    ? 'Cette page est au-delà de la dernière : revenez en arrière pour retrouver des profils.'
                    : filtered
                      ? 'Aucun profil ne correspond à ces critères.'
                      : 'Aucun partage enregistré sur le serveur.'
                }
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 2 }}>
                <Button
                  onClick={() => {
                    go(filters, Math.max(1, pageState.page - 1));
                  }}
                  disabled={pageState.page <= 1 || list.busy}
                >
                  Page précédente
                </Button>
                <Typography sx={{ fontSize: 14, color: 'tokens.textMuted', textAlign: 'center' }}>
                  {describeFamiliesPage(pageState)}
                </Typography>
                <Button
                  onClick={() => {
                    go(filters, pageState.page + 1);
                  }}
                  disabled={!hasNextPage(pageState) || list.busy}
                >
                  Page suivante
                </Button>
              </Box>
            </>
          )}
        </Card>
      </Box>
    );

    return (
      <MasterDetail
        list={listPane}
        selectedId={detail.selectedId}
        detailLabel="Fiche du profil"
        onClose={detail.close}
        renderDetail={(id) => <FamilyDetailPanel babyId={id} token={token} onRetry={refresh} />}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        eyebrow="Utilisateurs"
        title="Familles et partages"
        subtitle="Un profil bébé et les comptes qui y ont accès, avec leur rôle de coparentalité. En lecture seule : retirer un accès se fait depuis l’application."
        actions={
          granted ? (
            <Button icon={RefreshCw} onClick={refresh} disabled={list.busy}>
              Actualiser
            </Button>
          ) : undefined
        }
      />
      {renderBody()}
    </Box>
  );
};
